// URL state + present mode — wave-2 system map (G5). Stacks onto the canvas
// root next to the engine (data-controller="ui--flow ui--flow-url-state" —
// the same stacking pattern as ui--flow-sync): the canvas element IS this
// controller's element, so present mode can toggle the root state class and
// the [data-flow-chrome] wrapper's inline display directly, with no CSS
// !important fight against the wrapper's inline `display: contents`.
//
// URL/hash ownership split (one writer per param — mirror of the header in
// flow_story_controller.js):
//   ?present=          THIS controller (present-mode deep-link + toggle)
//   #focus=            THIS controller — READ ONLY at boot; hosts author it
//   #view= / #beat=    flow_story_controller.js (G4 guided views)
//   #route= / #lens=   flow_inspect_controller.js (INSPECT track)
// Every write goes through history.replaceState — viewer state is
// replace-state, never history spam (archify ruling carried into wave 2).
// Boot reads: ?present=1 enters present mode; #focus=<nodeId> selects the
// node and fits the view.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this._present = false
    // The chrome wrapper's inline display before present mode hid it (the
    // canvas ERB renders `display: contents` — restored verbatim on exit).
    this._chromeDisplay = null

    this._onKeydown = (event) => this._handleKeydown(event)
    document.addEventListener("keydown", this._onKeydown)

    this._boot()
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
    if (this._retryTimer) clearTimeout(this._retryTimer)
  }

  // The engine shares this controller's element (stacked controllers), so
  // resolution is a same-element lookup — no outlet attribute needed.
  _engine() {
    try {
      return this.application.getControllerForElementAndIdentifier(this.element, "ui--flow")
    } catch {
      return null
    }
  }

  // Engine upgrade is normally synchronous (same element, listed first),
  // but ONE short retry covers a slow Stimulus scan — the status_bar's M4
  // no-permanent-polling pattern (a missing engine never loops forever).
  _boot() {
    const flow = this._engine()
    if (!flow) {
      this._retryTimer = setTimeout(() => {
        const retry = this._engine()
        if (retry) this._applyBootState(retry)
      }, 500)
      return
    }
    this._applyBootState(flow)
  }

  _applyBootState(flow) {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""))
    const focusId = hash.get("focus")
    if (focusId && flow.getNode(focusId)) {
      flow.selectNode(focusId)
      flow.fitView()
    }

    if (new URLSearchParams(window.location.search).get("present") === "1") {
      this.enterPresent()
    }
  }

  togglePresent() {
    this._present ? this.exitPresent() : this.enterPresent()
  }

  enterPresent() {
    if (this._present) return
    this._present = true
    this.element.classList.add("ui-flow--present")
    const chrome = this.element.querySelector("[data-flow-chrome]")
    if (chrome) {
      this._chromeDisplay = chrome.style.display
      chrome.style.display = "none"
    }
    this._writePresentParam(true)
    // Re-measure chrome BEFORE fitView (wave-8 A2): the engine's chrome-aware
    // fitView reads its CACHED _chromeInsets, and none of the four recompute
    // sites fires on this toggle — without the announcement the fit uses the
    // pre-hide insets (a phantom bottom band for the now-hidden status bar).
    // dispatchEvent is synchronous, so the engine re-measures (hidden
    // children → zero insets) inside the dispatch, before fitView reads the
    // cache. This element IS the canvas root, so its id is exactly the
    // canvasId the engine's scoping check compares.
    document.dispatchEvent(new CustomEvent("ui--flow:chromechange", { detail: { canvasId: this.element.id } }))
    this._engine()?.fitView()
  }

  exitPresent() {
    if (!this._present) return
    this._present = false
    this.element.classList.remove("ui-flow--present")
    const chrome = this.element.querySelector("[data-flow-chrome]")
    if (chrome) chrome.style.display = this._chromeDisplay ?? "contents"
    this._chromeDisplay = null
    this._writePresentParam(false)
    // Mirror of enterPresent (wave-8 A2): restored chrome must re-enter the
    // insets BEFORE the fit, or the fit runs on stale zero insets.
    document.dispatchEvent(new CustomEvent("ui--flow:chromechange", { detail: { canvasId: this.element.id } }))
    this._engine()?.fitView()
  }

  // Esc exits present mode. Deliberately NOT the engine's form-field guard:
  // in present mode the chrome (and every form field inside it) is hidden,
  // so Esc never steals a keystroke from an input. When present mode is off
  // this listener is inert — the engine's own Escape (clear selection) is
  // untouched.
  _handleKeydown(event) {
    if (event.key !== "Escape" || !this._present) return
    this.exitPresent()
  }

  // ?present= is this controller's one param (see the header ownership
  // table). replaceState only; the hash (view/beat/focus/…) is preserved.
  _writePresentParam(present) {
    const params = new URLSearchParams(window.location.search)
    if (present) params.set("present", "1")
    else params.delete("present")
    const query = params.toString()
    const url = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
    window.history.replaceState(null, "", url)
  }
}
